/**
 * Best-effort PII scrubber for logs leaving the device.
 *
 * Masks values that could identify a user or grant access:
 * - Bearer / Authorization tokens (header + URL form)
 * - Long opaque tokens (Stripe sk_, Pusher app keys, JWTs)
 * - URL query parameters carrying secrets (?token=, &access_token=, etc.)
 * - JSON fields whose KEY is sensitive (token / access_token / refresh_token
 *   / api_key / secret / authorization / password)
 * - Emails and phone numbers
 * - GPS coordinates: JSON form ("lat": 48.85), URL form (lat=48.85, lng=2.35)
 *   and "lat,lng" tuples — all rounded to ~1km (2 decimals)
 *
 * Does NOT touch:
 * - Numeric IDs (id / client_id / user_id / demande_id) — needed for support
 * - Demande IDs, request paths, error messages
 *
 * RGPD: applied client-side BEFORE any upload, so PII never leaves the device.
 *
 * Unit tests live in log-scrubber.spec.ts. New regex must be accompanied by a
 * passing test case there.
 */

const TOKEN_REGEXES: Array<{ re: RegExp; replacement: string | ((m: string, ...g: string[]) => string) }> = [
  // Bearer tokens (header or in URL)
  { re: /\bBearer\s+[A-Za-z0-9._\-]+/g, replacement: "Bearer [REDACTED]" },
  // Stripe keys (sk_live_, sk_test_, pk_live_, pk_test_, rk_*) — at least 16 chars after prefix
  { re: /\b(sk|pk|rk)_(live|test)_[A-Za-z0-9]{16,}\b/g, replacement: "[STRIPE_KEY]" },
  // JWTs (three base64url chunks separated by dots, header starts with eyJ)
  { re: /\beyJ[A-Za-z0-9_\-]+\.[A-Za-z0-9_\-]+\.[A-Za-z0-9_\-]+/g, replacement: "[JWT]" },
  // Pusher app keys ("key":"…hex…")
  { re: /"key"\s*:\s*"[a-f0-9]{20,}"/g, replacement: '"key":"[PUSHER_KEY]"' },
  // URL query params carrying secrets — covers ? and & form, captures the
  // separator and the param name so the result keeps the URL intact for debug.
  {
    re: /([?&])(token|access_token|refresh_token|id_token|auth|authorization|api_key|apikey|client_secret|secret|code|password)=([^&\s"'<>]+)/gi,
    replacement: (_m, sep: string, name: string) => `${sep}${name}=[REDACTED]`,
  },
  // JSON-style sensitive fields. KEYS only (case-insensitive). Value can be a
  // string, number, or null.
  {
    re: /"(token|access_token|refresh_token|id_token|api_key|apikey|client_secret|secret|password|authorization)"\s*:\s*("(?:[^"\\]|\\.)*"|\d+|null)/gi,
    replacement: (_m, key: string) => `"${key}":"[REDACTED]"`,
  },
];

const EMAIL_RE = /[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}/g;
// French international + 10-digit phone numbers, anchored to digit boundaries
// to avoid catastrophic backtracking. Previous form `({…}){2,4}` was vulnerable
// to ReDoS on strings of "(((((...". Now: optional intl prefix, then 2-4
// digit-only groups (separated by whitespace, dot or dash), then a 2-4 digit
// tail. No nested quantifiers, no overlapping alternatives.
const PHONE_RE = /(?:\+\d{1,3}[\s.\-]?)?(?:\d{2,4}[\s.\-]?){2,4}\d{2,4}/g;

// Hard cap on scrubber input — beyond this length any regex pass is wasteful
// and risky. Single log line >32KB is almost certainly a JSON.stringify of a
// huge object. We keep the tail (most recent context) and truncate marker.
const MAX_SCRUB_INPUT = 32_768;

// "lat":48.8566 or "latitude":48.8566 — round to 2 decimals (~1km)
const LATLNG_JSON_RE = /"(lat|latitude|lng|longitude|long)"\s*:\s*(-?\d+\.\d+)/g;
// URL form: lat=48.85&lng=2.35 — rounds in place.
const LATLNG_URL_RE = /\b(lat|latitude|lng|longitude|long)=(-?\d+\.\d+)/gi;
// "48.8566,2.3522" tuples (only inside reasonable string boundaries).
const LATLNG_TUPLE_RE = /\b(-?\d{1,3}\.\d{4,})\s*,\s*(-?\d{1,3}\.\d{4,})\b/g;

function roundCoord(value: string): number {
  return Math.round(parseFloat(value) * 100) / 100;
}

export function scrubLogText(input: string): string {
  if (!input) return "";
  // Cap input size BEFORE any regex pass to prevent ReDoS / pathological CPU.
  let out =
    input.length > MAX_SCRUB_INPUT
      ? input.slice(-MAX_SCRUB_INPUT) + "…[truncated]"
      : input;

  for (const { re, replacement } of TOKEN_REGEXES) {
    if (typeof replacement === "string") {
      out = out.replace(re, replacement);
    } else {
      out = out.replace(re, replacement as any);
    }
  }

  out = out.replace(EMAIL_RE, "[email]");

  // Apply phone redaction AFTER email — emails contain @ which won't match
  // phone, but the phone regex is permissive and could otherwise eat parts
  // of unrelated numeric content. Guard by digit count: at least 8.
  out = out.replace(PHONE_RE, (match) => {
    const digits = match.replace(/\D/g, "");
    return digits.length >= 8 ? "[phone]" : match;
  });

  out = out.replace(LATLNG_JSON_RE, (_full, key, value) => `"${key}":${roundCoord(value)}`);
  out = out.replace(LATLNG_URL_RE, (_full, key, value) => `${key}=${roundCoord(value)}`);
  out = out.replace(LATLNG_TUPLE_RE, (_full, lat, lng) => `${roundCoord(lat)},${roundCoord(lng)}`);

  return out;
}
